/**
 * Cognito group names (the `cognito:groups` claim values) and the app sections
 * each group is allowed to access. Single source of truth for route-level
 * authorization — used by both the route guards and the navigation menu.
 */

import { FeatureFlags } from '@/environments/environment.type';

import { WASHINGTON_TEAM } from './known-teams.constants';

export enum UserGroup {
  SCOUT = 'Scout',
  TRAINER = 'Trainer',
  ANALYST = 'Analyst',
  BOARD = 'Board',
  GM = 'GM',
  ADMIN = 'Admin',
}

/** Gated route paths (must match the `path` values in app.routes.ts). */
export enum AppRoute {
  PRE_GAME = 'pre-game',
  PLAYGROUND = 'playground',
  SCOUTING = 'scouting',
  SCOUTING_TACTIK = 'scouting-tactik',
  COMMANDS = 'commands',
}

interface RouteLabelEntry {
  route: AppRoute;
  label: string;
}

/** Human-readable section names, used in the access-denied screen / menu. */
export const ROUTE_LABELS: readonly RouteLabelEntry[] = [
  { route: AppRoute.PRE_GAME, label: 'My Team' },
  { route: AppRoute.PLAYGROUND, label: 'Playground' },
  { route: AppRoute.SCOUTING, label: 'Scouting' },
  { route: AppRoute.SCOUTING_TACTIK, label: 'Scouting Tactik' },
  { route: AppRoute.COMMANDS, label: 'Commands' },
];

type RouteDeployFlag =
  | 'myTeamDeploy'
  | 'playgroundDeploy'
  | 'scoutingWSDeploy'
  | 'scoutingTactikDeploy'
  | 'controllingDeploy';

interface RouteFeatureFlagEntry {
  route: AppRoute;
  flag: RouteDeployFlag;
}

/** Deploy flag that shows/hides each section in the navigation menu. */
const ROUTE_FEATURE_FLAGS: readonly RouteFeatureFlagEntry[] = [
  { route: AppRoute.PRE_GAME, flag: 'myTeamDeploy' },
  { route: AppRoute.PLAYGROUND, flag: 'playgroundDeploy' },
  { route: AppRoute.SCOUTING, flag: 'scoutingWSDeploy' },
  { route: AppRoute.SCOUTING_TACTIK, flag: 'scoutingTactikDeploy' },
  { route: AppRoute.COMMANDS, flag: 'controllingDeploy' },
];

/** Routes that a fully-privileged group (Admin) can access. */
const ALL_ROUTES: AppRoute[] = Object.values(AppRoute);

/**
 * Default sections granted to a user whose groups match none of the known
 * groups below (including a user with no groups at all). Equivalent to the
 * Trainer/Analyst level — they see My Team and Playground, but not Scouting.
 */
const DEFAULT_ROUTES: AppRoute[] = [AppRoute.PRE_GAME, AppRoute.PLAYGROUND];

interface GroupRouteAccessEntry {
  group: UserGroup;
  routes: AppRoute[];
}

/** Maps each group to the sectionsAppRoute.PLAYGROUND it may access. Commands is open to every group. */
const GROUP_ROUTE_ACCESS: readonly GroupRouteAccessEntry[] = [
  { group: UserGroup.SCOUT, routes: [AppRoute.SCOUTING, AppRoute.SCOUTING_TACTIK] },
  { group: UserGroup.TRAINER, routes: [AppRoute.PRE_GAME, AppRoute.PLAYGROUND] },
  { group: UserGroup.ANALYST, routes: [AppRoute.PRE_GAME, AppRoute.PLAYGROUND] },
  { group: UserGroup.BOARD, routes: [AppRoute.COMMANDS] },
  { group: UserGroup.GM, routes: [AppRoute.COMMANDS] },
  { group: UserGroup.ADMIN, routes: ALL_ROUTES },
];

interface RouteTeamExclusionEntry {
  route: AppRoute;
  excludedTeamIds: number[];
}

/**
 * Team ids hard-blocked from a route regardless of the user's group — checked
 * independently of GROUP_ROUTE_ACCESS, so no group (including Admin) can bypass it.
 * See known-teams.constants.ts to add/change which team is excluded from what.
 */
const ROUTE_TEAM_EXCLUSIONS: readonly RouteTeamExclusionEntry[] = [
  { route: AppRoute.SCOUTING_TACTIK, excludedTeamIds: [WASHINGTON_TEAM.teamId] },
];

/** True when the given team is hard-blocked from the route, independent of group. */
export function isRouteExcludedForTeam(route: AppRoute, teamId: number | null | undefined): boolean {
  if (teamId == null) return false;
  const entry = ROUTE_TEAM_EXCLUSIONS.find((e: RouteTeamExclusionEntry) => e.route === route);
  return !!entry && entry.excludedTeamIds.includes(teamId);
}

/**
 * Resolve every section a user may access, as the union of all their known
 * groups, with any team-excluded route removed regardless of group. Falls
 * back to DEFAULT_ROUTES when none of their groups are recognized, so
 * unmapped/group-less users still get the baseline sections. Always returned in
 * menu (AppRoute declaration) order, so the first entry is the first visible tab.
 */
export function getAccessibleRoutes(groups: string[], teamId?: number | null): AppRoute[] {
  const routes: AppRoute[] = [];
  for (const group of groups) {
    const entry = GROUP_ROUTE_ACCESS.find((e: GroupRouteAccessEntry) => e.group === (group as UserGroup));
    if (!entry) continue;
    for (const route of entry.routes) {
      if (!routes.includes(route)) routes.push(route);
    }
  }
  const accessible = routes.length ? routes : DEFAULT_ROUTES;
  return ALL_ROUTES.filter((route: AppRoute) => accessible.includes(route) && !isRouteExcludedForTeam(route, teamId));
}

/** True when the user's groups (or the default fallback) grant the given route. */
export function canAccessRoute(groups: string[], route: AppRoute, teamId?: number | null): boolean {
  return getAccessibleRoutes(groups, teamId).includes(route);
}

/** True when the route's deploy flag is on (routes without a flag entry are always enabled). */
export function isRouteEnabledByFlags(route: AppRoute, flags: FeatureFlags): boolean {
  const entry = ROUTE_FEATURE_FLAGS.find((e: RouteFeatureFlagEntry) => e.route === route);
  return !entry || flags[entry.flag];
}

/**
 * First route the user can access (used as a safe landing/redirect target).
 * When flags are given, sections hidden from the menu are skipped.
 */
export function getFirstAllowedRoute(groups: string[], teamId?: number | null, flags?: FeatureFlags): AppRoute | null {
  const routes = getAccessibleRoutes(groups, teamId);
  return routes.find((route: AppRoute) => !flags || isRouteEnabledByFlags(route, flags)) ?? null;
}
